import React, { useEffect, useState, useRef } from 'react';
import { clsx } from 'clsx';
import { useElementInteraction } from '../hooks/useElementInteraction';
import { MoveHandle, ResizeHandle, DeleteButton, DuplicateButton } from './buttons';

export default function BaseElement({
  id,
  type,
  position = { x: 0, y: 0 },
  size = { width: 100, height: 100 },
  style = {},
  zIndex = 0,
  isSelected = false,
  isInlineEditing = false,
  children,
  onSelect,
  onMove,
  onResize,
  onDelete,
  onDuplicate,
  onUpdate,
  onZIndexChange,
  containerRef,
  aspectRatio = null
}) {
  const [currentZIndex, setCurrentZIndex] = useState(zIndex);
  const [isHoveredLocal, setIsHoveredLocal] = useState(false);
  const elementRef = useRef(null);

  if (!id || !type) {
    console.error('BaseElement: Missing required props', { id, type });
    return null;
  }

  useEffect(() => {
    if (elementRef.current) elementRef.current.style.zIndex = String(zIndex);
    setCurrentZIndex(zIndex);
  }, [id, zIndex]);

  const {
    isDragging,
    isResizing,
    handleDragStart,
    handleResizeStart,
    handleMouseMove,
    handleMouseUp
  } = useElementInteraction({
    initialPosition: position,
    initialSize: size,
    onMove,
    onResize,
    containerRef,
    elementType: type,
    aspectRatio
  });

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('pointermove', handleMouseMove);
      window.addEventListener('pointerup', handleMouseUp);
      window.addEventListener('pointercancel', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('pointermove', handleMouseMove);
        window.removeEventListener('pointerup', handleMouseUp);
        window.removeEventListener('pointercancel', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const handleSelect = (e) => {
    e.stopPropagation();
    // Close any open context menu when selecting a different element
    if (typeof window !== 'undefined' && window.__sbwCloseContextMenu) {
      window.__sbwCloseContextMenu();
    }
    if (typeof window !== 'undefined' && window.__sbwSuppressCanvasClick) {
      window.__sbwSuppressCanvasClick = false;
      return;
    }
    const target = e.target;
    if (
      target &&
      typeof target.closest === 'function' &&
      (target.closest('button') || target.closest('[data-role="control-button"]'))
    ) {
      return;
    }
    onSelect?.();
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect?.();
    if (window.__sbwShowContextMenu) {
      window.__sbwShowContextMenu(id, e.clientX, e.clientY);
    }
  };

  const safeStyle = { ...style };
  if (type === 'text' && safeStyle.pointerEvents === 'none') {
    safeStyle.pointerEvents = 'auto';
  }

  const effectiveZIndex =
    type === 'text' && (currentZIndex === undefined || currentZIndex === null || currentZIndex <= 1)
      ? 10
      : currentZIndex;

  return (
    <div
      ref={elementRef}
      className={clsx('element-base', isSelected && 'selected', isDragging && 'dragging', isResizing && 'resizing')}
      style={{
        position: 'absolute',
        top: `${position.y}px`,
        left: `${position.x}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        zIndex: effectiveZIndex,
        border: isSelected ? '1px solid #3B82F6' : (isHoveredLocal ? '1px solid #A5B4FC' : 'none'),
        cursor: isDragging ? 'grabbing' : 'move',
        overflow: 'visible',
        ...safeStyle,
      }}
      data-element-id={id}
      onMouseEnter={() => setIsHoveredLocal(true)}
      onMouseLeave={() => setIsHoveredLocal(false)}
      onContextMenu={handleContextMenu}
      onPointerUp={(e) => {
        const target = e.target;
        if (
          target &&
          typeof target.closest === 'function' &&
          (target.closest('button') || target.closest('[data-role="control-button"]'))
        ) {
          return;
        }
        try { e.currentTarget?.releasePointerCapture?.(e.pointerId); } catch {}
        handleMouseUp();
      }}
      onMouseUp={(e) => {
        const target = e.target;
        if (
          target &&
          typeof target.closest === 'function' &&
          (target.closest('button') || target.closest('[data-role="control-button"]'))
        ) {
          return;
        }
        handleMouseUp();
      }}
      onPointerDownCapture={(e) => {
        if (e.button !== 0) return;
        const target = e.target;
        // Check for resize handle - if found, let it handle the event
        if (
          target &&
          typeof target.closest === 'function' &&
          target.closest('.cursor-se-resize')
        ) {
          return;
        }
        if (
          target &&
          typeof target.closest === 'function' &&
          (target.closest('button') || target.closest('[data-role="control-button"]') || target.closest('textarea') || target.closest('input'))
        ) {
          return;
        }
        // Disable dragging for text elements (temporary)
        if (type === 'text') {
          return;
        }
        e.preventDefault();
        console.log('drag start');
        try {
          // Helps ensure we receive pointerup even if the pointer leaves the element
          e.currentTarget?.setPointerCapture?.(e.pointerId);
        } catch {}
        handleDragStart(e);
      }}
      onMouseDownCapture={(e) => {
        if (e.button !== 0) return;
        const target = e.target;
        // Check for resize handle - if found, let it handle the event
        if (
          target &&
          typeof target.closest === 'function' &&
          target.closest('.cursor-se-resize')
        ) {
          return;
        }
        if (
          target &&
          typeof target.closest === 'function' &&
          (target.closest('button') || target.closest('[data-role="control-button"]') || target.closest('textarea') || target.closest('input'))
        ) {
          return;
        }
        // Disable dragging for text elements (temporary)
        if (type === 'text') {
          return;
        }
        e.preventDefault();
        console.log('drag start');
        handleDragStart(e);
      }}
      onClick={handleSelect}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        const target = e.target;
        if (
          target &&
          typeof target.closest === 'function' &&
          (target.closest('button') || target.closest('[data-role="control-button"]') || target.closest('.cursor-se-resize'))
        ) {
          return;
        }

        // Prevent native text selection and default behaviors that block smooth dragging
        e.preventDefault();
        handleDragStart(e);
      }}
    >
      {children}
      
      {isSelected && (
        <>
          <DeleteButton onDelete={onDelete} />
          <DuplicateButton onDuplicate={onDuplicate} />
          <ResizeHandle 
            onMouseDown={handleResizeStart} 
            usePortal={false}
            containerRef={containerRef}
            position={position}
            size={size}
          />
        </>
      )}
    </div>
  );
}
